import { inject, Service } from '@angular/core';
import { ClientHttpService } from '@data-services/http/client-http.service';
import { NameMapEntry } from '@models/common';
import { Company, CompanyStatusMessageColors } from '@models/companies';
import { getFileNameFromContentDisposition } from '@utils';
import { PrintCompanyProgressReportRequest } from './print-company-progress-report-request.model';
import { PrintCompanyReportRequest } from './print-company-report-request.model';
import { PrintCompanyReportResponse } from './print-company-report-response.model';
import { PrintStatusMessagesReportRequest } from './print-status-messages-report-request.model';

@Service()
export class ClientCompanyDataService {
  private readonly http = inject(ClientHttpService);

  async getCompaniesAsync(projectId: string): Promise<Company[]> {
    return await this.http.getObjectAsync<Company[]>(`projects/${projectId}/companies`);
  }

  async getCompanyByIdAsync(companyId: string): Promise<Company> {
    return await this.http.getObjectAsync<Company>(`companies/${companyId}`);
  }

  async getCompanyNameMapAsync(projectId: string): Promise<NameMapEntry[]> {
    return await this.http.getObjectAsync<NameMapEntry[]>(`projects/${projectId}/companies/view/map/name`);
  }

  async getStatusMessageColorsAsync(projectId: string): Promise<CompanyStatusMessageColors[]> {
    return await this.http.getObjectAsync<CompanyStatusMessageColors[]>(`projects/${projectId}/companies/statusMessageColors`);
  }

  async printStatusMessagesReportAsync(companyId: string, request: PrintStatusMessagesReportRequest, fallbackFileName: string): Promise<PrintCompanyReportResponse> {
    return await this.postPdfAsync(`companies/${companyId}/statusMessages/pdf`, request, fallbackFileName);
  }

  async printCompanyProgressReportAsync(projectId: string, request: PrintCompanyProgressReportRequest, fallbackFileName: string): Promise<PrintCompanyReportResponse> {
    return await this.postPdfAsync(`projects/${projectId}/companies/progress/pdf`, request, fallbackFileName);
  }

  private async postPdfAsync(url: string, request: PrintCompanyReportRequest, fallbackFileName: string): Promise<PrintCompanyReportResponse> {
    const response = await this.http.postBlobAsync<PrintCompanyReportRequest>(url, request);
    const blob = response.body ?? new Blob();
    const fileName = getFileNameFromContentDisposition(response.headers.get('Content-Disposition'), fallbackFileName);
    return { blob, fileName };
  }
}
